import React, { useRef, useMemo, useEffect, useState } from 'react';
import * as THREE from 'three';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { finishUrl } from '../../utils/assetUtils';

/**
 * items: Array of { label, value, color, imgUrl }
 * dimension: size of the NxNxN block
 *
 * The largest-weight items get allocated the most "shell cubes."
 * Each shell cube shows its item’s image only on the outward-facing faces.
 */
export default function RubiksCubes({ items, dimension = 20 }) {
  return (
    <Canvas
      camera={{
        // Push the camera further back for a larger cube
        position: [0, dimension * 2, dimension * 4],
        near: 0.1,
        far: 2000
      }}
    >
      <color attach="background" args={['transparent']} />
      <ambientLight intensity={0.6} />
      <pointLight position={[0, 50, 50]} intensity={0.8} />
      <OrbitControls enableZoom enableRotate />
      <RubiksGroup items={items} dimension={dimension} />
    </Canvas>
  );
}

/**
 * This component handles:
 * 1) Figure out the shell cubes
 * 2) Sort items by weight & allocate those shell cubes
 * 3) Render each allocated shell cube
 */
function RubiksGroup({ items, dimension }) {
  const [loadedTextures, setLoadedTextures] = useState([]);

  // 1) Collect all coordinates on the "shell"
  //    (any x=0 or x=dim-1 or y=0 or y=dim-1 or z=0 or z=dim-1).
  const shellPositions = useMemo(() => {
    const coords = [];
    for (let x = 0; x < dimension; x++) {
      for (let y = 0; y < dimension; y++) {
        for (let z = 0; z < dimension; z++) {
          const isOnBoundary =
            x === 0 ||
            x === dimension - 1 ||
            y === 0 ||
            y === dimension - 1 ||
            z === 0 ||
            z === dimension - 1;
          if (isOnBoundary) {
            coords.push({ x, y, z });
          }
        }
      }
    }
    return coords;
  }, [dimension]);

  // 2) Sort items by descending weight & figure out how many shell cubes each gets
  const allocations = useMemo(() => {
    if (!items || items.length === 0) return [];

    // Sort by largest `value` first
    const sorted = [...items].sort((a, b) => b.value - a.value);

    // Count total shell cubes, sum of all weights
    const totalShell = shellPositions.length;
    const totalWeight = sorted.reduce((acc, it) => acc + it.value, 0);

    // Build an array [ { item, count }, ... ] proportionally
    const base = sorted.map((it) => {
      const fraction = totalWeight ? it.value / totalWeight : 0;
      return { item: it, count: Math.round(fraction * totalShell) };
    });

    // Check how many cubes we allocated in total
    let sumCount = base.reduce((acc, o) => acc + o.count, 0);

    // If we under-allocated (sumCount < totalShell), give the remainder
    // entirely to the largest item (which is base[0]) to ensure no blank section
    if (sumCount < totalShell) {
      const needed = totalShell - sumCount;
      base[0].count += needed;
      sumCount += needed;
    }
    // If we somehow over-allocated (sumCount > totalShell), remove from the smallest items upward
    if (sumCount > totalShell) {
      let excess = sumCount - totalShell;
      for (let i = base.length - 1; i >= 0 && excess > 0; i--) {
        const canRemove = Math.min(excess, base[i].count);
        base[i].count -= canRemove;
        excess -= canRemove;
      }
    }

    return base;
  }, [items, shellPositions]);

  // 3) Assign each shell position to an item in allocations (in a single pass)
  const assignedShellCubes = useMemo(() => {
    if (!allocations || allocations.length === 0) return [];

    const result = [];
    let posIndex = 0;
    // Allocate the shell positions item by item
    for (const { item, count } of allocations) {
      for (let i = 0; i < count; i++) {
        if (posIndex >= shellPositions.length) break;
        const { x, y, z } = shellPositions[posIndex];
        result.push({ x, y, z, item });
        posIndex++;
      }
    }
    return result;
  }, [allocations, shellPositions]);

  // 4) Preload textures for each item (just 1 image per item)
  useEffect(() => {
    if (!items || !items.length) return;
    const loader = new THREE.TextureLoader();
    const loadAll = async () => {
      const results = await Promise.all(
        items.map(async (it) => {
          if (!it.imgUrl) return null;
          return new Promise((resolve) => {
            loader.load(
              finishUrl(it.imgUrl),
              (texture) => {
                texture.generateMipmaps = true;
                texture.minFilter = THREE.LinearMipmapLinearFilter;
                texture.magFilter = THREE.LinearFilter;
                resolve(texture);
              },
              undefined,
              () => resolve(null) // If fails, we store null
            );
          });
        })
      );
      setLoadedTextures(results);
    };
    loadAll();
  }, [items]);

  // Build a quick lookup for each item => loaded texture
  const textureMap = useMemo(() => {
    const map = new Map();
    items.forEach((it, idx) => {
      map.set(it, loadedTextures[idx] || null);
    });
    return map;
  }, [items, loadedTextures]);

  // Single BoxGeometry reused for all cubes
  const geometry = useMemo(() => new THREE.BoxGeometry(1, 1, 1), []);

  return (
    <group>
      {assignedShellCubes.map(({ x, y, z, item }, i) => {
        // Center around (0,0,0)
        const half = (dimension - 1) / 2;
        const px = (x - half) * 1.02;
        const py = (y - half) * 1.02;
        const pz = (z - half) * 1.02;

        return (
          <SingleCube
            key={`cube-${i}`}
            geometry={geometry}
            item={item}
            texture={textureMap.get(item)}
            position={[px, py, pz]}
            x={x}
            y={y}
            z={z}
            dimension={dimension}
          />
        );
      })}
    </group>
  );
}

/**
 * SingleCube:
 * - We only display the item’s texture on faces that are outward.
 * - Outward face check:
 *    x=0 => -X face
 *    x=dimension-1 => +X face
 *    y=0 => -Y face
 *    y=dimension-1 => +Y face
 *    z=0 => -Z face
 *    z=dimension-1 => +Z face
 */
function SingleCube({ geometry, item, texture, position, x, y, z, dimension }) {
  const cubeRef = useRef();

  // Decide face materials. We'll generate up to 6 materials (for each face).
  // In Three.js, BoxGeometry faces are typically ordered:
  //   [ +X, -X, +Y, -Y, +Z, -Z ]
  const materials = useMemo(() => {
    // We ignore item.color here—just use white as fallback if no texture
    const fallbackMat = new THREE.MeshStandardMaterial({
      color: 'white'
    });

    // If we do have a texture, create a standard material with that texture
    const textureMat = texture
      ? new THREE.MeshStandardMaterial({ map: texture, color: 'white' })
      : fallbackMat;

    // Decide face by face if it’s outward
    const matArray = [];
    // +X => outward if x === dimension-1
    matArray[0] = x === dimension - 1 ? textureMat : fallbackMat;
    // -X => outward if x === 0
    matArray[1] = x === 0 ? textureMat : fallbackMat;
    // +Y => outward if y === dimension - 1
    matArray[2] = y === dimension - 1 ? textureMat : fallbackMat;
    // -Y => outward if y === 0
    matArray[3] = y === 0 ? textureMat : fallbackMat;
    // +Z => outward if z === dimension - 1
    matArray[4] = z === dimension - 1 ? textureMat : fallbackMat;
    // -Z => outward if z === 0
    matArray[5] = z === 0 ? textureMat : fallbackMat;

    return matArray;
  }, [texture, x, y, z, dimension]);

  // Optional subtle rotation for each cube
  // useFrame(() => {
  //   if (cubeRef.current) {
  //     cubeRef.current.rotation.y += 0.0005;
  //   }
  // });

  return (
    <mesh ref={cubeRef} geometry={geometry} material={materials} position={position} />
  );
}
